import { createContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { TEmojiId, TSetState } from '@src/types';
import { REACTIONS } from '@src/constants';

type EmojiAnimations = Record<TEmojiId, any>;

interface EmojiAnimationsContextValue {
  animations: EmojiAnimations;
  loading: boolean;
  error?: Error;
  isEmojiPickerVisible: boolean;
  setIsEmojiPickerVisible: TSetState<boolean>;
}

export const EmojiAnimationsContext = createContext<EmojiAnimationsContextValue | undefined>(undefined);

export const EmojiAnimationsProvider = ({ children }: { children: ReactNode }) => {
  const [animations, setAnimations] = useState<EmojiAnimations>(
    Object.fromEntries(REACTIONS.map((id) => [id, null])) as EmojiAnimations
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [isEmojiPickerVisible, setIsEmojiPickerVisible] = useState(false);

  const getEmojiUrl = useCallback((id: TEmojiId): string => {
    const baseUrl = 'https://fonts.gstatic.com/s/e/notoemoji/latest/';
    const emojiMapping: Record<TEmojiId, string> = {
      heart: '2764_fe0f',
      thumbsUp: '1f44d_1f3fb',
      clap: '1f44f_1f3fb',
      party: '1f389',
      monocle: '1f9d0',
      surprised: '1f62f',
      tearsOfJoy: '1f923',
      cryingFace: '1f622',
      thumbsDown: '1f44e_1f3fb',
    };
    return `${baseUrl}${emojiMapping[id]}/lottie.json`;
  }, []);

  useEffect(() => {
    const fetchAnimations = async () => {
      try {
        const animationData: EmojiAnimations = { ...animations };
        for (const reaction of REACTIONS) {
          const response = await fetch(getEmojiUrl(reaction));
          if (!response.ok) {
            throw new Error(`Error fetching emoji ${reaction}`);
          }
          animationData[reaction] = await response.json();
        }
        setAnimations(animationData);
      } catch (err: any) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchAnimations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <EmojiAnimationsContext.Provider
      value={{ animations, loading, error, isEmojiPickerVisible, setIsEmojiPickerVisible }}
    >
      {children}
    </EmojiAnimationsContext.Provider>
  );
};
